import { lazy, Suspense, type FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { _isNotEmpty, _notNil } from '@/littledash';

import { AnimalSexLabelToCode } from '@/model/Animal.utils';
import type { AnimalSexLabel } from '@/model/Animal.model';
import type { AnimalSex, AnimalSpecies } from '@/model/Animal.model';
import type { Cage } from '@/model/Cage.model';
import type { ID } from '@/model/Common.model';
import type { MetadataField, MetadataFieldValueTypes } from '@/model/Metadata.model';
import { MetadataTypes } from '@/model/Metadata.model';

import type { CustomGridSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { createMeta } from '@/support/hot';
import { createMetaV16 } from '@/support/hot-v16';
import type { HoTMeta } from '@/support/hot';
import Http from '@/support/http';
import { api as apiRoutes, web as webRoute } from '@/support/route';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema';
import { featuresSelector } from '@/support/Selectors';
import { MAX_CAGE_POPULATION } from '@/constants/cage';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import Button from '@/components/UI/Button';
import Header from '@/components/UI/Header';
import SubHeader from '@/components/UI/SubHeader';
import { successToast } from '@/helpers';

import type HotTable from '@handsontable//react';

import {
  fetchExactCageData,
  getMetaData,
  initAnimalSettings,
  initialAddAnimalData,
  updateStrainSelectDropdown,
} from '../Colony.utils';
import { initAnimalSettingsV16, initialAddAnimalDataV16 } from '../Colony.utils.v16';
import type { AddAnimalRowV16 } from '../Colony.utils.v16';
import '../Colony.scss';

const AddAnimalsTableV12 = lazy(() => import('./AddAnimalsTableV12'));
const AddAnimalsTableV16 = lazy(() => import('./AddAnimalsTableV16'));

interface AddAnimalRow {
  id: number;
  cage_name: string;
  cage_catalog: string;
  catalog: string;
  name: string;
  sex?: AnimalSex;
  dob?: string | null;
  species?: string;
  strain?: string;
  meta?: Record<string, MetadataFieldValueTypes>;
}

interface AnimalPostData {
  cage_id?: ID;
  name: string;
  dob?: string | null;
  sex?: AnimalSex;
  strain_id?: ID;
  meta?: Array<HoTMeta>;
}

const hotUpgradeSelector = createSelector([featuresSelector], (features) => ({
  hotUpgrade: features?.hot_upgrade ?? false,
  unknownSexEnabled: features?.unknown_sex === true,
}));

const AddAnimals: FC = () => {
  const { id: cageID } = useParams();
  const navigate = useNavigate();
  const { hotUpgrade, unknownSexEnabled } = useSelector(hotUpgradeSelector);

  const refV12 = useRef<HotTable>();
  const refV16 = useRef<HotTableRef>(null);

  const [data, setData] = useState<Array<AddAnimalRow | AddAnimalRowV16>>([]);
  const [settings, setSettings] = useState<CustomGridSettings | SpreadsheetV16Settings | null>(null);
  const [apiError, setApiError] = useState<Error | false>(false);
  const [metadata, setMetadata] = useState<Array<MetadataField>>([]);
  const [cageData, setCageData] = useState<Cage | undefined>();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [animalCount, setAnimalCount] = useState<number>(0);
  const [species, setSpecies] = useState<Array<AnimalSpecies>>([]);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');

  const getHotInstance = () => {
    if (hotUpgrade) {
      return refV16.current?.hotInstance ?? null;
    }
    return refV12.current?.hotInstance ?? null;
  };

  useEffect(() => {
    if (hotUpgrade) {
      const result = initAnimalSettingsV16(
        species,
        true,
        true,
        undefined,
        undefined,
        undefined,
        entityFieldRequiredMap,
        unknownSexEnabled
      );
      setSettings(result.settings);
    } else {
      const result = initAnimalSettings(
        species,
        true,
        true,
        undefined,
        undefined,
        undefined,
        entityFieldRequiredMap,
        unknownSexEnabled
      );
      setSettings(result.settings);
    }
  }, [species, entityFieldRequiredMap, hotUpgrade, unknownSexEnabled]);

  const patchMetadata = () => {
    if (_notNil(refV12.current) && _notNil(refV12.current.hotInstance)) {
      const hot = refV12.current.hotInstance;
      const col = hot.propToCol('species');
      const colData = hot.getDataAtCol(col);

      colData.forEach((value: string, row: number) => {
        updateStrainSelectDropdown(hot, species, row, value);
      });
      hot.render();
    }
  };

  useEffect(() => {
    if (_notNil(cageData)) {
      if (hotUpgrade) {
        setData(initialAddAnimalDataV16(cageData));
      } else {
        setData(initialAddAnimalData(cageData) as unknown as Array<AddAnimalRow>);
      }
      checkAnimalLimit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cageData, hotUpgrade]);

  const { response: speciesResponse } = useApiHook({
    endpoint: 'GET /api/v1/species',
    invokeOnInit: true,
    query: { include: ['strains'] },
    options: { onError: { throw: false, capture: true, toast: false } },
  });

  useEffect(() => {
    if (speciesResponse?.type === 'success') {
      setSpecies(speciesResponse?.body?.data as unknown as Array<AnimalSpecies>);
    }
  }, [speciesResponse]);

  const getInitialDependencies = async () => {
    setLoading(true);
    try {
      const metadataResponse = await getMetaData(MetadataTypes.subject_meta);
      const cage = await fetchExactCageData(cageID as ID);
      setMetadata((metadataResponse?.body ?? []) as Array<MetadataField>);
      setCageData(cage.data.data);
    } catch (error) {
      setApiError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInitialDependencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hotUpgrade && _notNil(refV12.current) && _notNil(refV12.current.hotInstance)) {
    const hot = refV12.current.hotInstance;
    hot.addHook('afterRemoveRow', (_: number, amount: number) => {
      checkAnimalLimit(true, amount);
    });
  }

  const afterAddRow = () => {
    setUpdating(true);
  };

  useEffect(() => {
    checkAnimalLimit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, updating]);

  const checkAnimalLimit = (removeRow = false, amountRemoved = 0) => {
    const hot = getHotInstance();
    const animalsToAdd = _notNil(hot) ? hot.countRenderedRows() : 1;
    if (_notNil(cageData) && _notNil(cageData.counts)) {
      const animalInCage = cageData.counts.animals ?? 0;
      if (removeRow) {
        setAnimalCount(animalCount - amountRemoved);
      } else {
        setAnimalCount(animalsToAdd + animalInCage);
      }
    } else {
      setAnimalCount(animalCount + animalsToAdd);
    }
    setUpdating(false);
  };

  const postAnimals = async (animals: Array<AnimalPostData>) => {
    return Http.post(apiRoutes('animals.createMany'), { animals });
  };

  const mapAnimalPostData = (rowData: Array<AddAnimalRow | AddAnimalRowV16>): Array<AnimalPostData> =>
    rowData.map(({ name, sex, dob, species: animalSpecies, strain, meta }) => {
      const returnData: AnimalPostData = {
        cage_id: cageData?.id,
        name,
        dob: dob ?? null,
      };
      if (_notNil(sex)) {
        returnData.sex = AnimalSexLabelToCode[sex as AnimalSexLabel];
      }
      if (_notNil(animalSpecies)) {
        const matchingSpecies = species.find((s) => s.name === animalSpecies);
        if (_notNil(matchingSpecies) && _isNotEmpty(matchingSpecies.strains)) {
          const matchingStrain = matchingSpecies.strains?.find((s) => s.name === strain);
          if (_notNil(matchingStrain)) {
            returnData.strain_id = matchingStrain.id;
          }
        }
      }
      if (_isNotEmpty(meta) && _notNil(settings)) {
        if (hotUpgrade) {
          const v16Settings = settings as SpreadsheetV16Settings;
          if (_notNil(v16Settings.columns)) {
            returnData.meta = createMetaV16(meta, v16Settings.columns) as Array<HoTMeta>;
          }
        } else {
          const v12Settings = settings as CustomGridSettings;
          returnData.meta = createMeta(meta, v12Settings.columns);
        }
      }
      return returnData;
    });

  const saveAnimals = async () => {
    setLoading(true);
    const animals = mapAnimalPostData(data);
    try {
      await postAnimals(animals);
      navigate('/colony');
      successToast(`Successfully added ${animals.length > 1 ? 'animals' : 'animal'}`);
    } catch (error) {
      setApiError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  const addRowConfig = {
    tooltip: animalCount >= MAX_CAGE_POPULATION ? 'Maximum cage population reached.' : '',
    title: 'Animal',
  };

  const defaultNewRowParams = [
    { col: 2, value: cageData?.name ?? '' },
    { col: 4, value: `Animal ${animalCount + 1}` },
  ];

  return (
    <div className="h-100">
      {apiError !== false && (
        <ApiErrorBanner
          className="mb4"
          title="There was an error with your request"
          text="An error has occurred when submitting your request, please try again later. If this keeps occurring please contact support."
          error={apiError}
        />
      )}
      <SubHeader linkToText="Colony" link={webRoute('colony')} />
      <div className="ph4">
        <Header mainHeaderText="Add new animals" />
        <div className="flex-row items-start flex flex-wrap mb3">
          <p className="lh-copy set-width">
            <span className="mr2">Add new animals to {cageData?.name}</span>
          </p>
        </div>
      </div>
      {loading ? (
        <div style={{ height: 650 }}>
          <Loading txt="Updating cage..." />
        </div>
      ) : (
        _notNil(settings) && (
          <Suspense fallback={<Loading />}>
            <div className="ow-spreadsheet-styles">
              {hotUpgrade ? (
                <AddAnimalsTableV16
                  data={data as unknown as Array<Record<string, unknown>>}
                  settings={settings as SpreadsheetV16Settings}
                  innerRef={refV16 as React.RefObject<HotTableRef>}
                  metadata={metadata}
                  setMetadata={setMetadata}
                  setInvalid={setIsInvalid}
                  addRow={addRowConfig}
                  afterAddRow={afterAddRow}
                  defaultNewRowParams={defaultNewRowParams}
                />
              ) : (
                <AddAnimalsTableV12
                  data={data as unknown as Array<Record<string, unknown>>}
                  settings={settings as CustomGridSettings}
                  innerRef={refV12}
                  metadata={metadata}
                  setMetadata={setMetadata}
                  addRow={addRowConfig}
                  afterAddRow={afterAddRow}
                  defaultNewRowParams={defaultNewRowParams}
                  patchMetadataAfterSettingsUpdated={patchMetadata}
                  setInvalid={setIsInvalid}
                />
              )}
            </div>
          </Suspense>
        )
      )}
      <div className="pa4">
        <Button
          onClick={saveAnimals}
          disabled={loading || isInvalid}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
        >
          Save
        </Button>
        <Button plain className="ml3" onClick={() => navigate('/colony')}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default AddAnimals;
